import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { blockedForTrial } from './components/TrialUpgrade';
import { GoogleMap, Circle } from '@react-google-maps/api';
import { CompanyPinLayer, PinCompany } from './components/CompanyPinLayer';
import { IS_RECMAPPER } from './product';
import MapFilterBar from './MapFilterBar';
import { AISearchBar } from './AISearchBar';
import { FilterBarV2 } from './FilterBarV2';
import { FilterState } from './types/filters';
import { filterCompaniesByState, filterCandidatesByState } from './utils/filterMatcher';
import * as XLSX from 'xlsx';

interface Company {
  id: number;
  company_name: string;
  office_address?: string;
  latitude?: number;
  longitude?: number;
  formatted_address?: string;
  candidate_count?: number;
  hiring_manager_count?: number;
  website?: string;
  url?: string;
  logo_url?: string;
  industry?: string;
  subindustry?: string[] | string;
  description?: string;
  specialties?: string;
  employee_count?: number;
  founded?: number;
  follower_count?: number;
  type?: string;
  pin_color?: string;
  category?: string;
  slogan?: string;
  primary_category?: string;
  about?: string;
  investors?: string;
  funding?: string;
  headquarters?: string;
  crunchbase_url?: string;
  company_size_formatted?: string;
  annual_revenue?: string;
  business_type?: string[] | string;
  business_stage?: string;
  primary_offerings?: string[] | string;
  revenue_streams?: string[] | string;
  pattern_tags?: string[] | string;
  scale_scope?: string;
  custom_data?: Record<string, any>;
}

interface Candidate {
  id: number;
  name: string;
  position: string;
  employment_type: string;
  current_contract_type?: string; 
  working_style: string;
  company: string;
  address: string;
  company_location: string;
  li_profile_url: string;
  summary: string;
  headline: string;
  phone: string;
  email: string;
  personal_email: string;
  all_skills: string[];
  current_role_description: string;
}

interface HiringManager {
  id: number;
  name: string;
  job_title: string;
  email: string;
  personal_email: string;
  phone: string;
  org: string;
  li_profile_url: string;
  li_company_url: string;
  summary: string;
  headline: string;
  location: string;
  is_starred?: boolean;
  tags?: string[];
}

interface FilterCondition {
  id: string;
  columns: string[];
  operator: string;
  value: string;
  valueMax?: string;
  entityType: 'company' | 'candidate';
}

interface MapContainerProps {
  selectedProject: {
    id: number;
    name: string;
  };
  initialFilters?: FilterState | null;
  onNavigateToDataTable?: (filters: FilterState) => void;
}

interface GroupedCompany {
  company_name: string;
  locations: Company[];
  totalCandidates: number;
  filteredCandidates: number;
  expanded: boolean;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%' // Use full height of parent flex container
};

const defaultCenter = {
  lat: 51.5074,
  lng: -0.1278
};

const MapContainer: React.FC<MapContainerProps> = ({ selectedProject, initialFilters, onNavigateToDataTable }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [totalCompanyCount, setTotalCompanyCount] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  // RecMapper loads pins without the long text fields (a country library is
  // tens of thousands of companies); the clicked company's are fetched here.
  useEffect(() => {
    if (!IS_RECMAPPER || !selectedCompany || (selectedCompany as any).description !== undefined) return;
    const id = selectedCompany.id;
    fetch(`/api/companies/${selectedProject.id}/company/${id}/enriched`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setSelectedCompany((prev) => (prev && prev.id === id
          ? { ...prev, description: d.description ?? null, specialties: d.specialties ?? null } as Company
          : prev));
      })
      .catch(() => {});
  }, [selectedCompany, selectedProject.id]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [allCandidates, setAllCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [hiringManagers, setHiringManagers] = useState<HiringManager[]>([]);
  const [activeTab, setActiveTab] = useState<'company' | 'candidates' | 'managers'>('company');
  const [loading, setLoading] = useState(true);
  const [rightPanelView, setRightPanelView] = useState<'list' | 'details'>('list');
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(2);
  const [selectedPerson, setSelectedPerson] = useState<Candidate | HiringManager | null>(null);
  const [personType, setPersonType] = useState<'candidate' | 'manager' | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<FilterCondition[]>([]);
  const [appliedEmploymentTypes, setAppliedEmploymentTypes] = useState<string[]>([]);
  const [customColumns, setCustomColumns] = useState<{column_key: string; column_label: string; column_type: string}[]>([]);

// New unified filter state (matches TableView)
const [aiFilterState, setAiFilterState] = useState<FilterState>(
  initialFilters || {
    groups: [],
    employmentTypes: [],
  }
);

// Radius search state
const [showRadiusSearch, setShowRadiusSearch] = useState(false);
const [radiusLocation, setRadiusLocation] = useState(initialFilters?.radiusSearch?.location || '');
const [radiusDistance, setRadiusDistance] = useState(initialFilters?.radiusSearch?.distance || 25);
const [radiusType, setRadiusType] = useState<'company' | 'candidate' | 'manager'>(initialFilters?.radiusSearch?.type || 'company');
const [radiusCenterCoords, setRadiusCenterCoords] = useState<{lat: number, lng: number} | null>(null);

// Temporary state for radius search inputs (to prevent lag while typing)
const [tempRadiusLocation, setTempRadiusLocation] = useState(radiusLocation);
const [tempRadiusDistance, setTempRadiusDistance] = useState(radiusDistance);
const [tempRadiusType, setTempRadiusType] = useState(radiusType);

  const [companyCandidateCounts, setCompanyCandidateCounts] = useState<Map<number, {total: number, filtered: number}>>(new Map());
  const [companyNameCandidateCounts, setCompanyNameCandidateCounts] = useState<Map<string, {total: number, filtered: number}>>(new Map());
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
const [companySortBy, setCompanySortBy] = useState<'name' | 'count-desc' | 'count-asc'>('count-desc');
const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
const [enriching, setEnriching] = useState<number | null>(null);
const [starredManagerIds, setStarredManagerIds] = useState<Set<number>>(new Set());
const [enrichmentMessage, setEnrichmentMessage] = useState<string | null>(null);
const [showEnrichMenu, setShowEnrichMenu] = useState<number | null>(null);
const [showPinColorModal, setShowPinColorModal] = useState(false);
const [selectedPinColor, setSelectedPinColor] = useState('#3B82F6');
const [pinColorPalette, setPinColorPalette] = useState<Array<{name: string, hex: string}>>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
const [pinGroups, setPinGroups] = useState<Array<{color: string, name: string | null, isVisible: boolean}>>([]);
const [visibleColors, setVisibleColors] = useState<Set<string>>(new Set());
const [visitedCompanyIds, setVisitedCompanyIds] = useState<Set<number>>(new Set());
// The map object for the pin layer, and the same-spot group whose list is open.
const [mapObj, setMapObj] = useState<google.maps.Map | null>(null);
const [pinGroup, setPinGroup] = useState<{ companies: Company[]; filter: string } | null>(null);
const [loadedColors, setLoadedColors] = useState<Set<string>>(new Set());
const [filterGroupsCollapsed, setFilterGroupsCollapsed] = useState(false);

// Apply color filter - this now sets filteredCompanies directly
const applyColorFilter = useCallback(() => {
  console.log('=== APPLYING COLOR FILTER ===');
  console.log('visibleColors:', Array.from(visibleColors));
  console.log('companies to filter:', companies.length);
  console.log('Sample company pin_colors:', companies.slice(0, 5).map(c => c.pin_color));

  if (visibleColors.size === 0) {
    console.log('No colors selected, showing empty');
    setFilteredCompanies([]);
    return;
  }

  const filtered = companies.filter(c =>
    c.latitude &&
    c.longitude &&
    visibleColors.has(c.pin_color || '#3B82F6')
  );

  console.log('filtered companies:', filtered.length);
  console.log('Companies filtered out (first 3):', companies.slice(0, 3).map(c => ({
    name: c.company_name,
    pin_color: c.pin_color,
    hasLatLng: !!(c.latitude && c.longitude),
    colorInVisible: visibleColors.has(c.pin_color || '#3B82F6')
  })));
  setFilteredCompanies(filtered);
}, [visibleColors, companies]);

// Apply filter whenever visibleColors or companies change
useEffect(() => {
  applyColorFilter();
}, [applyColorFilter]);

// Auto-zoom to fit filtered companies when filters are applied
useEffect(() => {
  // Only zoom if:
  // 1. We have filters applied (not initial state)
  // 2. We have filtered companies with coordinates
  // 3. Map is loaded
  const hasFilters = aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0 || aiFilterState.radiusSearch;

  if (hasFilters && filteredCompanies.length > 0 && mapRef.current) {
    // Filter companies that have valid coordinates
    const companiesWithCoords = filteredCompanies.filter(c => c.latitude && c.longitude);

    if (companiesWithCoords.length > 0) {
      // Small delay to ensure markers are rendered
      setTimeout(() => {
        zoomToFilteredCompanies(companiesWithCoords);
      }, 100);
    }
  }
}, [filteredCompanies, aiFilterState]);

// Client-side filtering (matches TableView logic)
const applyClientSideFilters = useCallback((candidatesToFilter: Candidate[]): Candidate[] => {
console.log('🔍 applyClientSideFilters called:', {
    candidateCount: candidatesToFilter.length,
    employmentTypes: aiFilterState.employmentTypes,
    filterGroups: aiFilterState.groups.length,
    fullState: aiFilterState
  });

  let filtered = [...candidatesToFilter];

  // 1. Apply Employment Type filters
  if (aiFilterState.employmentTypes.length > 0) {
    filtered = filtered.filter(candidate => {
      const candidateType = (candidate as any).current_contract_type || candidate.employment_type || '';
      return aiFilterState.employmentTypes.some(type => 
        candidateType.toLowerCase().includes(type.toLowerCase())
      );
    });
  }

  // 2. Apply Filter Groups (candidate filters only for now)
  if (aiFilterState.groups.length > 0) {
    for (const group of aiFilterState.groups) {
      if (group.conditions.length === 0 || group.entityType !== 'candidate') continue;

      filtered = filtered.filter(candidate => {
        if (group.logic === 'OR') {
          return group.conditions.some(condition => {
            const value = (candidate as any)[condition.column];
            if (!value) return condition.operator === 'is_empty';
            if (condition.operator === 'is_not_empty') return true;
            
            const filterValue = condition.value.toLowerCase();
            const itemValue = Array.isArray(value) 
              ? value.join(' ').toLowerCase() 
              : String(value).toLowerCase();
            
            return itemValue.includes(filterValue);
          });
        } else {
          return group.conditions.every(condition => {
            const value = (candidate as any)[condition.column];
            if (!value) return condition.operator === 'is_empty';
            if (condition.operator === 'is_not_empty') return true;
            
            const filterValue = condition.value.toLowerCase();
            const itemValue = Array.isArray(value) 
              ? value.join(' ').toLowerCase() 
              : String(value).toLowerCase();
            
            return itemValue.includes(filterValue);
          });
        }
      });
    }
  }

  return filtered;
}, [aiFilterState]);

  const mapRef = useRef<google.maps.Map | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const findMatchingCompanyLocation = (candidateLocation: string, companyAddress: string): boolean => {
    if (!candidateLocation || !companyAddress) return false;
    
    const excludeWords = ['united', 'kingdom', 'uk', 'usa', 'america', 'states', 'great', 'britain'];
    
    const candidateWords = candidateLocation.toLowerCase()
      .split(/[\s,]+/)
      .filter(w => w.length > 3 && !excludeWords.includes(w));
    
    const companyWords = companyAddress.toLowerCase()
      .split(/[\s,]+/)
      .filter(w => w.length > 3 && !excludeWords.includes(w));
    
    const matches = candidateWords.filter(word => companyWords.includes(word));
    
    return matches.length >= 1 && matches.some(w => w.length >= 5);
  };

const fetchCompanyCandidateCounts = useCallback(async () => {
  try {
    const hasFilters = aiFilterState.employmentTypes.length > 0 || aiFilterState.groups.length > 0;
console.log('=== FETCHING COUNTS ===');
console.log('aiFilterState.employmentTypes:', aiFilterState.employmentTypes);
console.log('aiFilterState.groups:', aiFilterState.groups);
console.log('hasFilters:', hasFilters);
    
    // Build filter params if filters are applied
    let queryString = '';
    if (hasFilters) {
      const params = new URLSearchParams();
      
if (aiFilterState.employmentTypes.length > 0 || aiFilterState.groups.length > 0) {
  params.append('filterType', 'candidates');
}
if (aiFilterState.employmentTypes.length > 0) {
  params.append('employmentType', aiFilterState.employmentTypes.join(','));
}
if (aiFilterState.groups.length > 0) {
        
        appliedFilters.forEach(filter => {
          if (filter.entityType === 'candidate') {
            if (filter.columns.includes('position') || filter.columns.includes('headline') || filter.columns.includes('summary')) {
              params.append('profileKeywords', filter.value);
            }
            if (filter.columns.includes('all_skills')) {
              params.append('skills', filter.value);
            }
            if (filter.columns.includes('working_style')) {
              params.append('workType', filter.value);
            }
          }
        });
      }
      
      queryString = '?' + params.toString();
    }
    
    // Fetch per-company-ID counts (returns {total, filtered} for each company ID)
console.log('Fetching URL:', `/api/companies/${selectedProject.id}/candidate-counts${queryString}`);
    const response = await fetch(`/api/companies/${selectedProject.id}/candidate-counts${queryString}`, {
      headers: getAuthHeaders()
    });
    
    if (response.ok) {
      const data = await response.json();
      const idCounts = new Map<number, {total: number, filtered: number}>();
      
      // Store per-ID counts
      Object.keys(data).forEach(companyId => {
        idCounts.set(parseInt(companyId), data[companyId]);
      });
      
      setCompanyCandidateCounts(idCounts);
      
// Aggregate by company name for the grouped list view
// All offices of the same company now share the same count (from backend)
const nameCounts = new Map<string, {total: number, filtered: number}>();

companies.forEach(company => {
  const counts = data[company.id];
  if (counts && !nameCounts.has(company.company_name)) {
    // Use first office's count (all offices have same count now)
    nameCounts.set(company.company_name, {
      total: counts.total,
      filtered: counts.filtered
    });
  }
});
      
      setCompanyNameCandidateCounts(nameCounts);
    }
  } catch (error) {
    console.error('Error fetching candidate counts:', error);
  }

}, [selectedProject.id, aiFilterState, companies]);

// Filter companies based on both company and candidate filters
const filterCompaniesByMatchingCandidates = useCallback(async () => {
  if (aiFilterState.groups.length === 0 && aiFilterState.employmentTypes.length === 0) {
    if (!aiFilterState.radiusSearch?.location) {
      // No filters - reset to show all companies (applying color filter)
      console.log('✅ No filters active, resetting to show all companies');
      applyColorFilter();
    } else {
      // Only radius filter - don't re-filter, radius search already set filteredCompanies
      console.log('✅ Only radius filter active, keeping radius-filtered companies');
    }
    return;
  }

  try {
    console.log('🎯 Filtering companies with filter state:', aiFilterState);

    // Step 1: Apply company-level filters directly to companies
    const companyFilteredCompanies = filterCompaniesByState(companies, aiFilterState);
    console.log('📍 Companies after company-level filters:', companyFilteredCompanies.length, 'of', companies.length);

    // Step 2: Check if there are any candidate-level filters
    const hasCandidateFilters =
      aiFilterState.employmentTypes.length > 0 ||
      aiFilterState.groups.some(g => g.conditions.some(c => c.entityType === 'candidate'));

    if (!hasCandidateFilters) {
      // Only company filters - use filtered companies directly
      console.log('✅ Using only company filters');
      setFilteredCompanies(companyFilteredCompanies);
      return;
    }

    // Step 3: Apply candidate-level filters
    console.log('🔍 Applying candidate filters...');
    const response = await fetch(`/api/candidates/${selectedProject.id}`, {
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const allCandidates = await response.json();

      // Apply filters to candidates
      const matchingCandidates = applyClientSideFilters(allCandidates);

      console.log('Matching candidates:', matchingCandidates.length, 'of', allCandidates.length);

      // Get unique company names from matching candidates with counts
      const matchingCompanyCounts = new Map<string, number>();
      matchingCandidates.forEach(c => {
        const companyName = c.company || (c as any).current_company;
        if (companyName) {
          matchingCompanyCounts.set(companyName, (matchingCompanyCounts.get(companyName) || 0) + 1);
        }
      });

      console.log('Companies with matching candidates:', matchingCompanyCounts.size);
      
     // Update company name counts with filtered counts
const updatedNameCounts = new Map<string, {total: number, filtered: number}>();
companyNameCandidateCounts.forEach((counts, companyName) => {
  const filteredCount = matchingCompanyCounts.get(companyName) || 0;
  updatedNameCounts.set(companyName, {
    total: counts.total,
    filtered: filteredCount
  });
});

setCompanyNameCandidateCounts(updatedNameCounts);

// Debug log
console.log('📊 Updated company name counts:', Array.from(updatedNameCounts.entries()).slice(0, 5));

// ALSO update per-ID counts for office locations
const updatedIdCounts = new Map<number, {total: number, filtered: number}>();
companies.forEach(company => {
  const companyFiltered = matchingCompanyCounts.get(company.company_name) || 0;
  const existingCounts = companyCandidateCounts.get(company.id);
  if (existingCounts) {
    updatedIdCounts.set(company.id, {
      total: existingCounts.total,
      filtered: companyFiltered
    });
  }
});

setCompanyCandidateCounts(updatedIdCounts);

      // Step 4: Intersect company-filtered companies with candidate-filtered companies
      // Companies must pass BOTH filters
      const companyFilteredNames = new Set(companyFilteredCompanies.map(c => c.company_name));
      const companiesToShow = companyFilteredCompanies.filter(company => {
        const matchingCount = matchingCompanyCounts.get(company.company_name);
        return matchingCount && matchingCount > 0 && companyFilteredNames.has(company.company_name);
      });

      console.log('✅ Final filtered companies:', companiesToShow.length, 'of', companies.length);
      console.log('   - Passed company filters:', companyFilteredCompanies.length);
      console.log('   - Have matching candidates:', matchingCompanyCounts.size);
      setFilteredCompanies(companiesToShow);
    }
  } catch (error) {
    console.error('Error filtering companies:', error);
  }
}, [selectedProject.id, aiFilterState, applyClientSideFilters, companies]);

// Run company filtering when filters change (including when cleared)
useEffect(() => {
  filterCompaniesByMatchingCandidates();
}, [aiFilterState, filterCompaniesByMatchingCandidates]);

// Sync temp radius state when panel opens
useEffect(() => {
  if (showRadiusSearch) {
    setTempRadiusLocation(radiusLocation);
    setTempRadiusDistance(radiusDistance);
    setTempRadiusType(radiusType);
  }
}, [showRadiusSearch, radiusLocation, radiusDistance, radiusType]);

// Apply initial filters when companies and candidates finish loading
useEffect(() => {
  // Only run if we have initialFilters and data is loaded
  if (initialFilters && companies.length > 0 && allCandidates.length > 0) {
    console.log('🎯 Applying initial filters on Map:', initialFilters);
    // The filterCompaniesByMatchingCandidates will be called by the above useEffect
    // since aiFilterState is already set from initialFilters
    if (initialFilters.groups.length > 0 || initialFilters.employmentTypes.length > 0) {
      filterCompaniesByMatchingCandidates();
    }
  }
}, [companies.length, allCandidates.length, initialFilters, filterCompaniesByMatchingCandidates]);

// Fetch companies progressively by color
const fetchCompaniesByColors = useCallback(async (colors: string[]) => {
  if (colors.length === 0) {
    setCompanies([]);
    setFilteredCompanies([]);
    return;
  }
  
  const fetchWithRetry = async (retryCount = 0, maxRetries = 3) => {
    try {
      setLoading(true);
      
      // Check for auth token first
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token available');
        setLoading(false);
        return;
      }
      
      // Only fetch colors we haven't loaded yet
      const colorsToFetch = colors.filter(c => !loadedColors.has(c));
      
      if (colorsToFetch.length > 0) {
        console.log('Fetching companies for colors:', colorsToFetch);
        
        const response = await fetch(
          `/api/companies/${selectedProject.id}/by-colors?colors=${colorsToFetch.map(c => encodeURIComponent(c)).join(',')}${IS_RECMAPPER ? '&lean=1' : ''}`,
          { 
            headers: getAuthHeaders(),
            signal: AbortSignal.timeout(30000) // 30 second timeout
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        if (response.ok) {
          const newCompanies = await response.json();
          console.log('=== API RESPONSE DEBUG ===');
          console.log('Loaded companies:', newCompanies.length);
          console.log('First company (if any):', newCompanies[0]);
          console.log('Colors requested:', colorsToFetch);
          console.log('Sample pin_colors from response:', newCompanies.slice(0, 5).map((c: Company) => c.pin_color));
          console.log('========================');

          // Merge with existing companies (avoid duplicates)
          setCompanies(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = newCompanies.filter((c: Company) => !existingIds.has(c.id));
            const allCompanies = [...prev, ...uniqueNew];
            
            // Update total count
            const uniqueNames = new Set(allCompanies.map(c => c.company_name));
            setTotalCompanyCount(uniqueNames.size);
            
            return allCompanies;
          });
          
          // Mark these colors as loaded
          setLoadedColors(prev => {
            const newSet = new Set(prev);
            colorsToFetch.forEach(c => newSet.add(c));
            return newSet;
          });
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching companies by color:', error);
      
      // Retry with exponential backoff
      if (retryCount < maxRetries) {
        const delay = 1000 * (retryCount + 1); // 1s, 2s, 3s
        console.log(`Retrying in ${delay}ms... (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => fetchWithRetry(retryCount + 1, maxRetries), delay);
      } else {
        console.error('Max retries reached, giving up');
        setLoading(false);
      }
    }
  };
  
  fetchWithRetry();
}, [selectedProject.id, loadedColors]);

const fetchPinGroups = async () => {
  try {
    const response = await fetch(`/api/pin-groups/project/${selectedProject.id}`, {
      headers: getAuthHeaders()
    });
    if (response.ok) {
      const groups = await response.json();
      setPinGroups(groups);
    }
  } catch (error) {
    console.error('Error fetching pin groups:', error);
  }
};

// Initial load: fetch pin groups
useEffect(() => {
  if (selectedProject?.id) {
    fetchPinGroups();
  }
}, [selectedProject?.id]);

// Fetch custom columns for the selected project
useEffect(() => {
  const fetchCustomColumns = async () => {
    if (!selectedProject?.id) return;

    try {
      const response = await fetch(`/api/custom-columns/${selectedProject.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const cols = await response.json();
        setCustomColumns(cols);
      }
    } catch (error) {
      console.error('Failed to fetch custom columns:', error);
    }
  };

  fetchCustomColumns();
}, [selectedProject?.id]);

// When pin groups are loaded, initialize visible colors and fetch data
useEffect(() => {
  if (pinGroups.length > 0 && visibleColors.size === 0 && companies.length === 0) {
    console.log('Initializing pin groups:', pinGroups);
    
    // Check for saved state
    const saved = localStorage.getItem(`pinGroups_${selectedProject.id}`);
    
    let colorsToShow: Set<string>;
    if (saved) {
      console.log('Loading saved colors:', saved);
      colorsToShow = new Set(JSON.parse(saved));
    } else {
      console.log('No saved state, showing all colors');
      // Default: show all colors
      colorsToShow = new Set(pinGroups.map(g => g.color));
    }
    
    console.log('Setting visible colors:', Array.from(colorsToShow));
    setVisibleColors(colorsToShow);
    fetchCompaniesByColors(Array.from(colorsToShow));
  }
}, [pinGroups, visibleColors.size, selectedProject.id, fetchCompaniesByColors]);

// When visible colors change, fetch any new colors
useEffect(() => {
  if (visibleColors.size > 0) {
    fetchCompaniesByColors(Array.from(visibleColors));
  }
}, [visibleColors, fetchCompaniesByColors]);

useEffect(() => {
    if (filteredCompanies.length > 0) {
      fetchCompanyCandidateCounts();
    }
  }, [filteredCompanies, fetchCompanyCandidateCounts]);

// Load color palette for pin color picker
  useEffect(() => {
    const fetchColorPalette = async () => {
      try {
        const response = await fetch('/api/companies/color-palette');
        if (response.ok) {
          const data = await response.json();
          setPinColorPalette(data.palette || []);
        }
      } catch (error) {
        console.error('Error fetching color palette:', error);
      }
    };
    
    fetchColorPalette();
  }, []);

 useEffect(() => {
    const setInitialMapLocation = async () => {
      try {
        // Fetch user's location from IP
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          
          // Set map center to user's city/country
          const userCenter = {
            lat: data.latitude,
            lng: data.longitude
          };
          
          setMapCenter(userCenter);
          
          // Set zoom based on country vs city detection
          // If we have a city, zoom closer (zoom 10)
          // If only country, zoom out more (zoom 6)
          const zoomLevel = data.city ? 10 : 6;
          setMapZoom(zoomLevel);
          
          if (mapRef.current) {
            mapRef.current.panTo(userCenter);
            mapRef.current.setZoom(zoomLevel);
          }
          
          console.log(`Map centered on ${data.city || data.country_name}`);
        }
      } catch (error) {
        console.error('Could not get user location, using default:', error);
        // Silently fall back to default world view
      }
    };
    
    // Only run once on mount
    setInitialMapLocation();
  }, []);

const getCompanyColumns = () => [
  { key: 'company_name', label: 'Company Name', type: 'text' as const, category: 'company' },
  { key: 'tags', label: 'Tags', type: 'array' as const, category: 'company' },
  { key: 'industry', label: 'Industry', type: 'text' as const, category: 'company' },
  { key: 'subindustry', label: 'Sub-Industry', type: 'array' as const, category: 'company' },
  { key: 'category', label: 'Country', type: 'text' as const, category: 'company' },
  { key: 'slogan', label: 'Slogan', type: 'text' as const, category: 'company' },
  { key: 'primary_category', label: 'Primary Category', type: 'text' as const, category: 'company' },
  { key: 'headquarters', label: 'Headquarters', type: 'text' as const, category: 'company' },
  { key: 'specialties', label: 'Specialties', type: 'text' as const, category: 'company' },
  { key: 'description', label: 'Description', type: 'text' as const, category: 'company' },
  { key: 'enhanced_description', label: 'AI Description', type: 'text' as const, category: 'company' },
  { key: 'about', label: 'About', type: 'text' as const, category: 'company' },
  { key: 'funding', label: 'Funding', type: 'text' as const, category: 'company' },
  { key: 'funding_range', label: 'Funding Range', type: 'text' as const, category: 'company' },
  { key: 'investors', label: 'Investors', type: 'text' as const, category: 'company' },
  { key: 'employee_count', label: 'Employees', type: 'number' as const, category: 'company' },
  { key: 'company_size_formatted', label: 'Company Size', type: 'text' as const, category: 'company' },
  { key: 'founded', label: 'Founded', type: 'number' as const, category: 'company' },
  { key: 'follower_count', label: 'Followers', type: 'number' as const, category: 'company' },
  { key: 'type', label: 'Company Type', type: 'text' as const, category: 'company' },
  { key: 'business_type', label: 'Business Type', type: 'array' as const, category: 'company' },
  { key: 'business_stage', label: 'Business Stage', type: 'text' as const, category: 'company' },
  { key: 'annual_revenue', label: 'Annual Revenue', type: 'text' as const, category: 'company' },
  { key: 'primary_offerings', label: 'Primary Offerings', type: 'array' as const, category: 'company' },
  { key: 'revenue_streams', label: 'Revenue Streams', type: 'array' as const, category: 'company' },
  { key: 'pattern_tags', label: 'Pattern Tags', type: 'text' as const, category: 'company' },
  { key: 'scale_scope', label: 'Scale & Scope', type: 'text' as const, category: 'company' },
];

const getCandidateColumns = () => [
  // Primary Info
  { key: 'name', label: 'Name', type: 'text' as const, category: 'primary' },  // Changed
  { key: 'tags', label: 'Tags', type: 'array' as const, category: 'primary' },
  { key: 'headline', label: 'Headline', type: 'text' as const, category: 'primary' },
  { key: 'summary', label: 'Summary', type: 'text' as const, category: 'primary' },
  { key: 'current_position', label: 'Current Role', type: 'text' as const, category: 'primary' },
  { key: 'current_position_description', label: 'Role Description', type: 'text' as const, category: 'primary' },
  { key: 'current_company', label: 'Current Company', type: 'text' as const, category: 'primary' },

  // Career History
  { key: 'previous_position_1', label: 'Previous Role 1', type: 'text' as const, category: 'career' },  // Changed
  { key: 'previous_company_1', label: 'Previous Company 1', type: 'text' as const, category: 'career' },
  { key: 'previous_position_1_description', label: 'Previous Role 1 Description', type: 'text' as const, category: 'career' },
  { key: 'previous_position_2', label: 'Previous Role 2', type: 'text' as const, category: 'career' },
  { key: 'previous_position_3', label: 'Previous Role 3', type: 'text' as const, category: 'career' },
  { key: 'current_contract_type', label: 'Employment Type', type: 'text' as const, category: 'career' },
  { key: 'years_at_current_company', label: 'Years at Company', type: 'number' as const, category: 'career' },
  { key: 'total_experience_years', label: 'Total Experience', type: 'number' as const, category: 'career' },
  
  // Education & Certs
  { key: 'education_1', label: 'Education', type: 'text' as const, category: 'education' },  // Changed
  { key: 'degree_1', label: 'Degree', type: 'text' as const, category: 'education' },
  { key: 'field_of_study_1', label: 'Field of Study', type: 'text' as const, category: 'education' },
  { key: 'certification_1', label: 'Top Certification', type: 'text' as const, category: 'education' },
  
  // Skills
  { key: 'top_skills_5', label: 'Top 5 Skills', type: 'text' as const, category: 'skills' },
  { key: 'top_skills_10', label: 'Top 10 Skills', type: 'text' as const, category: 'skills' },
  { key: 'all_skills_text', label: 'All Skills', type: 'text' as const, category: 'skills' },
  
  // Other Fields
  { key: 'position', label: 'Position (Legacy)', type: 'text' as const, category: 'legacy' },
  { key: 'working_style', label: 'Work Style', type: 'text' as const, category: 'legacy' },
];

  const handleApplyFilters = async (
    filters: FilterCondition[], 
    employmentTypes: string[],
    radiusType: 'company' | 'candidate' | null,
    location: string,
    distance: number
  ) => {
    try {
      setAppliedFilters(filters);
      setAppliedEmploymentTypes(employmentTypes);

      const backendFilters = [...filters];
      
      if (employmentTypes.length > 0) {
        backendFilters.push({
          id: 'employment-' + Date.now(),
          columns: ['employment_type'],
          operator: 'contains',
          value: employmentTypes.join('|'),
          entityType: 'candidate'
        });
      }

      if (filters.length === 0 && employmentTypes.length === 0 && !location) {
        setFilteredCompanies(companies);
        return;
      }

      const response = await fetch(`/api/companies/${selectedProject.id}/clay-filtered`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          filters: backendFilters,
          radiusType,
          location,
          distance
        })
      });

      if (response.ok) {
        const filteredData = await response.json();
        setFilteredCompanies(filteredData);
      
    // ADD THIS: Auto-zoom to filtered results
      if (filteredData.length > 0 && location && distance > 0) {
        zoomToFilteredCompanies(filteredData);
      }
    }
    } catch (error) {
      console.error('Error applying filters:', error);
      setFilteredCompanies(companies);
    }
  };

// ADD THIS FUNCTION RIGHT HERE
  const zoomToFilteredCompanies = (companies: Company[]) => {
    if (!mapRef.current || companies.length === 0) return;
    
    // Calculate bounds from actual company locations
    const bounds = new google.maps.LatLngBounds();
    
    companies.forEach(company => {
      if (company.latitude && company.longitude) {
        bounds.extend(new google.maps.LatLng(company.latitude, company.longitude));
      }
    });
    
    // Fit map to show all filtered companies
    mapRef.current.fitBounds(bounds);
    
    // Optional: Add padding so pins aren't right at the edge
    const padding = { top: 50, right: 50, bottom: 50, left: 450 }; // Extra left padding for panel
    mapRef.current.fitBounds(bounds, padding);
    
    console.log(`Map zoomed to ${companies.length} filtered companies`);
  };

const handleBulkTag = async (tag: string) => {
  try {
    // Get all company IDs from filtered companies
    const companyIds = filteredCompanies.map(c => c.id);
    
    const response = await fetch(`/api/companies/${selectedProject.id}/bulk-tag`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        companyIds: companyIds,
        tags: [tag]
      })
    });
    
    if (response.ok) {
      alert(`✅ Tagged ${filteredCompanies.length} companies with "${tag}"`);
    } else {
      alert('❌ Failed to tag companies');
    }
  } catch (error) {
    console.error('Error bulk tagging:', error);
    alert('❌ Failed to tag companies');
  }
};

const handleBulkColorChange = async (color: string) => {
  try {
    // Group by company name and change all offices
    const companyNames = Array.from(new Set(filteredCompanies.map(c => c.company_name)));
    
    for (const companyName of companyNames) {
      const response = await fetch(`/api/companies/${selectedProject.id}/bulk-color`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          companyName: companyName,
          allOffices: true,
          color: color
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to change color for ${companyName}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    alert(`✅ Changed pin color for ${companyNames.length} companies (all offices)`);
    
    // Reload companies to show new colors
    const colorsToFetch = Array.from(visibleColors);
    await fetchCompaniesByColors(colorsToFetch);
    
  } catch (error) {
    console.error('Error bulk color change:', error);
    alert('❌ Failed to change pin colors');
  }
};

  const handleCompanySelect = useCallback(async (company: Company) => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollTop);
    }

    setSelectedCompany(company);
    setRightPanelView('details');
    setSelectedPerson(null);
    setPersonType(null);
    if (!rightPanelOpen) setRightPanelOpen(true);

    // Mark company as visited
    setVisitedCompanyIds(prev => new Set(prev).add(company.id));

    const newCenter = {
      lat: Number(company.latitude),
      lng: Number(company.longitude)
    };
    setMapCenter(newCenter);

    // Preserve current zoom level instead of hardcoding to 12
    const currentZoom = mapRef.current?.getZoom() || 12;
    // Only zoom in if currently zoomed out beyond level 10
    const targetZoom = currentZoom < 10 ? 12 : currentZoom;
    setMapZoom(targetZoom);

    if (mapRef.current) {
      mapRef.current.panTo(newCenter);
      mapRef.current.setZoom(targetZoom);
    }
    
    try {
      const candidatesResponse = await fetch(`/api/candidates/${selectedProject.id}?company_id=${company.id}`, {
        headers: getAuthHeaders()
      });
      
      if (candidatesResponse.ok) {
        const candidatesData = await candidatesResponse.json();
      
console.log('=== CANDIDATES API RESPONSE ===');
  console.log('Company ID:', company.id);
  console.log('Company Name:', company.company_name);
  console.log('Candidates returned:', candidatesData.length);
  if (candidatesData.length > 0) {
    console.log('First candidate:', candidatesData[0]);
  }
  
        const sorted = candidatesData.sort((a: Candidate, b: Candidate) => {
const aMatches = findMatchingCompanyLocation(a.company_location, company.office_address || '');
const bMatches = findMatchingCompanyLocation(b.company_location, company.office_address || '');
          if (aMatches && !bMatches) return -1;
          if (!aMatches && bMatches) return 1;
          return 0;
        });
        
      setAllCandidates(sorted);

// Apply filters using the new system
const filtered = applyClientSideFilters(sorted);
setCandidates(filtered);
setFilteredCandidates(filtered);

console.log('Candidates after filtering:', {
  total: sorted.length,
  filtered: filtered.length,
  allCandidates: sorted.length,
  filterGroups: aiFilterState.groups.length,
  employmentTypes: aiFilterState.employmentTypes.length
});
      }

      const managersResponse = await fetch(`/api/hiring-managers/${selectedProject.id}?company_id=${company.id}`, {
        headers: getAuthHeaders()
      });
      
      if (managersResponse.ok) {
        const managersData = await managersResponse.json();
        setHiringManagers(managersData);

        // Initialize starred managers Set
        const starredIds = new Set<number>(
          managersData.filter((m: HiringManager) => m.is_starred).map((m: HiringManager) => m.id)
        );
        setStarredManagerIds(starredIds);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }

}, [rightPanelOpen, selectedProject?.id, aiFilterState, applyClientSideFilters]);

  // Refresh company data (candidates + managers) without resetting selected person
  // Used after enrichment to show updated contact data in the side panel
  const refreshCompanyData = useCallback(async (company: Company, currentPersonId?: number, currentPersonType?: 'candidate' | 'manager') => {
    try {
      const [candidatesResponse, managersResponse] = await Promise.all([
        fetch(`/api/candidates/${selectedProject.id}?company_id=${company.id}`, {
          headers: getAuthHeaders()
        }),
        fetch(`/api/hiring-managers/${selectedProject.id}?company_id=${company.id}`, {
          headers: getAuthHeaders()
        })
      ]);

      if (candidatesResponse.ok) {
        const candidatesData = await candidatesResponse.json();
        const sorted = candidatesData.sort((a: Candidate, b: Candidate) => {
          const aMatches = findMatchingCompanyLocation(a.company_location, company.office_address || '');
          const bMatches = findMatchingCompanyLocation(b.company_location, company.office_address || '');
          if (aMatches && !bMatches) return -1;
          if (!aMatches && bMatches) return 1;
          return 0;
        });

        setAllCandidates(sorted);
        const filtered = applyClientSideFilters(sorted);
        setCandidates(filtered);
        setFilteredCandidates(filtered);

        // Update selectedPerson with fresh data if they were a candidate
        if (currentPersonId && currentPersonType === 'candidate') {
          const updatedPerson = sorted.find((c: Candidate) => c.id === currentPersonId);
          if (updatedPerson) {
            setSelectedPerson(updatedPerson);
          }
        }
      }

      if (managersResponse.ok) {
        const managersData = await managersResponse.json();
        setHiringManagers(managersData);

        const starredIds = new Set<number>(
          managersData.filter((m: HiringManager) => m.is_starred).map((m: HiringManager) => m.id)
        );
        setStarredManagerIds(starredIds);

        // Update selectedPerson with fresh data if they were a manager
        if (currentPersonId && currentPersonType === 'manager') {
          const updatedPerson = managersData.find((m: HiringManager) => m.id === currentPersonId);
          if (updatedPerson) {
            setSelectedPerson(updatedPerson);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing company data:', error);
    }
  }, [selectedProject?.id, aiFilterState, applyClientSideFilters]);

  const handleBackToList = () => {
    setRightPanelView('list');
    setSelectedCompany(null);
    setSelectedPerson(null);
    setPersonType(null);
    setCandidates([]);
    setAllCandidates([]);
    setFilteredCandidates([]);
    setHiringManagers([]);
    
    setMapCenter(defaultCenter);
    setMapZoom(2);
    
    if (mapRef.current) {
      mapRef.current.panTo(defaultCenter);
      mapRef.current.setZoom(2);
    }

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollPosition;
      }
    }, 0);
  };

  const handlePersonClick = (person: Candidate | HiringManager, type: 'candidate' | 'manager') => {
    setSelectedPerson(person);
    setPersonType(type);
  };

  const handleBackToPeople = () => {
    setSelectedPerson(null);
    setPersonType(null);
  };

  const handleToggleStar = async (managerId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent opening the detail view

    // Get the current state from the manager object, not the Set
    const manager = hiringManagers.find(m => m.id === managerId);
    if (!manager) return;

    const isCurrentlyStarred = manager.is_starred || false;

    // Optimistically update UI
    const newStarredIds = new Set(starredManagerIds);
    if (isCurrentlyStarred) {
      newStarredIds.delete(managerId);
    } else {
      newStarredIds.add(managerId);
    }
    setStarredManagerIds(newStarredIds);

    // Update local manager data
    const updatedManagers = hiringManagers.map(m =>
      m.id === managerId ? { ...m, is_starred: !isCurrentlyStarred } : m
    );
    setHiringManagers(updatedManagers);

    // Update selectedPerson if it's the same manager
    if (selectedPerson && personType === 'manager' && (selectedPerson as HiringManager).id === managerId) {
      setSelectedPerson({ ...selectedPerson, is_starred: !isCurrentlyStarred } as HiringManager);
    }

    try {
      const token = localStorage.getItem('token');

      // Call API to persist starred state
      const response = await fetch(`/api/hiring-managers/${managerId}/star`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          is_starred: !isCurrentlyStarred
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update star status');
      }

      // Auto-tag as "decision maker" when starring
      if (!isCurrentlyStarred) {
        const existingTags = Array.isArray(manager.tags) ? manager.tags : [];

        if (!existingTags.includes('decision maker')) {
            // Add tag via API
            const tagResponse = await fetch(`/api/hiring-managers/${managerId}/tags`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                tags: [...existingTags, 'decision maker']
              })
            });

          if (tagResponse.ok) {
            // Update local data with new tag
            const updatedWithTags = hiringManagers.map(m =>
              m.id === managerId ? { ...m, tags: [...existingTags, 'decision maker'] } : m
            );
            setHiringManagers(updatedWithTags);

            // Update selectedPerson if it's the same manager
            if (selectedPerson && personType === 'manager' && (selectedPerson as HiringManager).id === managerId) {
              setSelectedPerson({ ...selectedPerson, tags: [...existingTags, 'decision maker'] } as HiringManager);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error toggling star:', error);
      // Revert on error
      setStarredManagerIds(starredManagerIds);
      setHiringManagers(hiringManagers.map(m =>
        m.id === managerId ? { ...m, is_starred: isCurrentlyStarred } : m
      ));
    }
  };

const handleChangePinColor = async (allOffices: boolean) => {
  if (!selectedCompany) return;
  
  try {
    const requestBody = allOffices ? {
      companyName: selectedCompany.company_name,
      allOffices: true,
      color: selectedPinColor
    } : {
      companyIds: [selectedCompany.id],
      color: selectedPinColor
    };
    
    const response = await fetch(`/api/companies/${selectedProject.id}/bulk-color`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    if (response.ok) {
      setShowPinColorModal(false);
      
      // Reload companies to show new color
      const colorsToFetch = Array.from(visibleColors);
      await fetchCompaniesByColors(colorsToFetch);
      
      alert(`✅ Changed pin color for ${allOffices ? 'all offices of ' + selectedCompany.company_name : 'this office'}`);
    } else {
      alert('❌ Failed to change pin color');
    }
  } catch (error) {
    console.error('Error changing pin color:', error);
    alert('❌ Failed to change pin color');
  }
};

const handleEnrichContact = async (personId: number, type: 'candidate' | 'manager', enrichType: 'work_email' | 'personal_email' | 'both_emails' | 'phone' | 'work_email_phone' | 'personal_email_phone' | 'all') => {
  try {
    setEnriching(personId);
    setShowEnrichMenu(null);
    
let costMessage = '';
switch (enrichType) {
  case 'work_email': costMessage = ' (1 credit)'; break;
  case 'personal_email': costMessage = ' (3 credits)'; break;
  case 'both_emails': costMessage = ' (4 credits)'; break;
  case 'phone': costMessage = ' (12 credits)'; break;
  case 'work_email_phone': costMessage = ' (13 credits)'; break;
  case 'personal_email_phone': costMessage = ' (15 credits)'; break;
  case 'all': costMessage = ' (16 credits)'; break;
}
    
    setEnrichmentMessage(`🔍 Searching 20+ data providers${costMessage}...`);

    const endpoint = type === 'candidate' 
      ? `/api/fullenrich/enrich/candidate/${personId}?type=${enrichType}`
      : `/api/fullenrich/enrich/manager/${personId}?type=${enrichType}`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const result = await response.json();

      // Check if this was an instant response (Cognism/Lusha/Apollo/ZoomInfo)
      if (result.instant) {
        // Instant provider - refresh data and show result while preserving selected person
        if (selectedCompany) {
          setEnrichmentMessage('🔄 Updating data...');
          await refreshCompanyData(selectedCompany, personId, type);
        }

        if (result.enrichment_status === 'found') {
          setEnrichmentMessage('✅ Contact data found and updated instantly!');
        } else {
          setEnrichmentMessage('⚠️ No contact data found for this person.');
        }

        if ((window as any).refreshCreditBalance) {
          (window as any).refreshCreditBalance();
        }

        setTimeout(() => {
          setEnrichmentMessage(null);
        }, 3000);
      } else {
        // FullEnrich async flow - wait for webhook
        setEnrichmentMessage(`⏳ Enriching${costMessage}... This takes 30-90 seconds. Feel free to browse, we'll update automatically.`);

        // Capture current context for the delayed refresh
        const refreshCompany = selectedCompany;
        const refreshPersonId = personId;
        const refreshPersonType = type;

        // Auto-refresh after 65 seconds
        setTimeout(async () => {
          if (refreshCompany) {
            setEnrichmentMessage('🔄 Refreshing data...');
            await refreshCompanyData(refreshCompany, refreshPersonId, refreshPersonType);
            setEnrichmentMessage('✅ Enrichment complete! Contact data updated.');

      // Refresh credit balance
      if ((window as any).refreshCreditBalance) {
        (window as any).refreshCreditBalance();
      }

            setTimeout(() => {
              setEnrichmentMessage(null);
            }, 3000);
          }
        }, 65000);
      }
    } else if (response.status === 402) {
      const error = await response.json();
      setEnrichmentMessage(`❌ Insufficient credits! You have ${error.balance} credits but need ${error.required}. Click the credits button above to purchase more.`);
      setTimeout(() => setEnrichmentMessage(null), 10000);
    } else {
      const error = await response.json();
      setEnrichmentMessage(`❌ Error: ${error.error || 'Failed to enrich contact'}`);
      setTimeout(() => setEnrichmentMessage(null), 5000);
    }
  } catch (error) {
    console.error('Enrichment error:', error);
    setEnrichmentMessage('❌ Failed to start enrichment. Please try again.');
    setTimeout(() => setEnrichmentMessage(null), 5000);
  } finally {
    setEnriching(null);
  }
};

  const toggleGroupExpanded = (companyName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyName)) {
        newSet.delete(companyName);
      } else {
        newSet.add(companyName);
      }
      return newSet;
    });
  };

  const getGroupedCompanies = useCallback((): GroupedCompany[] => {
    const grouped = new Map<string, GroupedCompany>();
    
    filteredCompanies.forEach(company => {
      const existing = grouped.get(company.company_name);
      
      if (existing) {
        existing.locations.push(company);
      } else {
        const counts = companyNameCandidateCounts.get(company.company_name);
        const totalCount = counts?.total || 0;
        const filteredCount = counts?.filtered || 0;
        
        grouped.set(company.company_name, {
          company_name: company.company_name,
          locations: [company],
          totalCandidates: totalCount,
          filteredCandidates: filteredCount,
          expanded: expandedGroups.has(company.company_name)
        });
      }
    });
    
    return Array.from(grouped.values());
  }, [filteredCompanies, companyNameCandidateCounts, expandedGroups]);

const getSortedGroupedCompanies = useCallback(() => {
  const groups = getGroupedCompanies();
  const hasFilters = aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0;
  
  switch (companySortBy) {
    case 'count-desc':
      return groups.sort((a, b) => {
        const aCount = hasFilters ? a.filteredCandidates : a.totalCandidates;
        const bCount = hasFilters ? b.filteredCandidates : b.totalCandidates;
        return bCount - aCount;
      });
    case 'count-asc':
      return groups.sort((a, b) => {
        const aCount = hasFilters ? a.filteredCandidates : a.totalCandidates;
        const bCount = hasFilters ? b.filteredCandidates : b.totalCandidates;
        return aCount - bCount;
      });
    case 'name':
    default:
      return groups.sort((a, b) => a.company_name.localeCompare(b.company_name));
  }
}, [getGroupedCompanies, companySortBy, aiFilterState]);
// Sorted once per data / sort change (not on every render — typing in a
// filter re-renders this component), and the side list draws a page at a
// time: a country library is tens of thousands of companies.
const sortedGroupedCompanies = useMemo(() => getSortedGroupedCompanies(), [getSortedGroupedCompanies]);
const LIST_PAGE = 200;
const [listLimit, setListLimit] = useState(LIST_PAGE);
useEffect(() => { setListLimit(LIST_PAGE); }, [sortedGroupedCompanies]);

  const handleLocationClick = (company: Company) => {
    handleCompanySelect(company);
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapObj(map);
  }, []);

if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Loading map data...</div>
          {loadingProgress.total > 0 && (
            <>
              <div className="text-sm text-gray-600 mb-2">
                {loadingProgress.loaded.toLocaleString()} of {loadingProgress.total.toLocaleString()} companies
              </div>
              <div className="w-64 bg-gray-200 rounded-full h-2.5 mx-auto">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%` }}
                ></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

const handleExportMapData = async () => {
  if (blockedForTrial()) return;
  try {
    const XLSX = await import('xlsx');
    
    // 1. Get unique company IDs from filtered companies
    const companyIds = filteredCompanies.map(c => c.id);
    const companyNames = new Set(filteredCompanies.map(c => c.company_name));
    
    // 2. Fetch ALL candidates and hiring managers for these companies
    const candidatesResponse = await fetch(`/api/candidates/${selectedProject.id}`, {
      headers: getAuthHeaders()
    });
    
    const managersResponse = await fetch(`/api/hiring-managers/${selectedProject.id}`, {
      headers: getAuthHeaders()
    });
    
    if (!candidatesResponse.ok || !managersResponse.ok) {
      alert('Failed to fetch data for export');
      return;
    }
    
    const allCandidates = await candidatesResponse.json();
    const allManagers = await managersResponse.json();
    
    // 3. Filter candidates and managers to only those in visible companies
    const exportCandidates = allCandidates.filter((c: any) => 
      companyNames.has(c.company || c.current_company)
    );
    
    // Apply current filters to candidates
    const filteredExportCandidates = applyClientSideFilters(exportCandidates);
    
    const exportManagers = allManagers.filter((m: any) =>
      companyNames.has(m.org)
    );
    
    // 4. Prepare data for Excel
    const companiesData = filteredCompanies.map(c => ({
      'Company Name': c.company_name,
      'Industry': c.industry || '',
      'Address': c.formatted_address || '',
      'Website': c.website || '',
      'LinkedIn': c.url || '',
      'Employees': c.employee_count || '',
      'Founded': c.founded || '',
      'Description': c.description || '',
      'Specialties': c.specialties || ''
    }));
    
    const candidatesData = filteredExportCandidates.map((c: any) => ({
  // Primary Info
  'Name': c.name || '',
  'Headline': c.headline || '',
  'Summary': c.summary || '',
  'Current Position': c.current_position || '',
  'Current Position Description': c.current_position_description || '',
  'Current Company': c.current_company || '',
  
  // Career History
  'Previous Position 1': c.previous_position_1 || '',
  'Previous Company 1': c.previous_company_1 || '',
  'Previous Position 1 Description': c.previous_position_1_description || '',
  'Employment Type': c.current_contract_type || '',
  'Years at Current Company': c.years_at_current_company || '',
  'Total Experience (Years)': c.total_experience_years || '',
  
  // Education
  'Education': c.education_1 || '',
  'Degree': c.degree_1 || '',
  'Field of Study': c.field_of_study_1 || '',
  
  // Skills
  'All Skills': c.all_skills_text || '',
  
  // Contact & Profile
  'Work Email': c.email || '',
  'Personal Email': c.personal_email || '',
  'Phone': c.phone || '',
  'LinkedIn': c.li_profile_url || '',
  // People from Search carry one location field; enriched CSV people carry city + country.
  'Location': c.location_full || (c.location_city ? `${c.location_city}, ${c.location_country}` : (c.address || c.current_company_location || '')),
  'Open to Work': c.open_to_work ? 'Yes' : 'No',
  'Company Industry': c.company_industry || ''
}));
    
    const managersData = exportManagers.map((m: any) => ({
      'Name': m.name,
      'Job Title': m.job_title,
      'Company': m.org,
      'Email': m.email || '',
      'Phone': m.phone || '',
      'LinkedIn': m.li_profile_url || '',
      'Location': m.location || ''
    }));
    
    // 5. Create workbook with 3 sheets
    const wb = XLSX.utils.book_new();
    
    const wsCompanies = XLSX.utils.json_to_sheet(companiesData);
    const wsCandidates = XLSX.utils.json_to_sheet(candidatesData);
    const wsManagers = XLSX.utils.json_to_sheet(managersData);
    
    XLSX.utils.book_append_sheet(wb, wsCompanies, 'Companies');
    XLSX.utils.book_append_sheet(wb, wsCandidates, 'Candidates');
    XLSX.utils.book_append_sheet(wb, wsManagers, 'Hiring Managers');
    
    // 6. Download file
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `map-export-${timestamp}.xlsx`);
    
    alert(`✅ Exported:\n${companiesData.length} companies\n${candidatesData.length} candidates\n${managersData.length} hiring managers`);
    
  } catch (error) {
    console.error('Export error:', error);
    alert('Failed to export data');
  }
};

  return (
    <div className="flex flex-col h-screen">
<div className="border-b bg-white flex-shrink-0">
  <AISearchBar
    onSearch={async (filterState: FilterState, explanation: string, canWiden?: boolean, isWidened?: boolean, searchQuery?: string, radiusSearchParam?: { location: string; distance: number; type: 'candidate' | 'company' }) => {
      console.log('AI Search on Map:', filterState, radiusSearchParam);
      setAiFilterState(filterState);

      // Handle AI-provided radius search
      if (radiusSearchParam) {
        console.log('📍 AI detected radius search on map:', radiusSearchParam);

        // Update radius search state
        setRadiusLocation(radiusSearchParam.location);
        setRadiusDistance(radiusSearchParam.distance);
        setRadiusType(radiusSearchParam.type);
        setTempRadiusLocation(radiusSearchParam.location);
        setTempRadiusDistance(radiusSearchParam.distance);
        setTempRadiusType(radiusSearchParam.type);

        // Apply the radius search via backend
        try {
          setLoading(true);

          // Geocode to get center for circle
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ address: radiusSearchParam.location }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const location = results[0].geometry.location;
              setRadiusCenterCoords({ lat: location.lat(), lng: location.lng() });
            }
          });

          const params = new URLSearchParams({
            filterType: radiusSearchParam.type === 'company' ? 'companies' : 'candidates',
            radiusType: radiusSearchParam.type,
            radiusLocation: radiusSearchParam.location,
            distance: radiusSearchParam.distance.toString()
          });

          const response = await fetch(
            `/api/companies/${selectedProject.id}/filtered?${params.toString()}`,
            {
              headers: getAuthHeaders(),
              cache: 'no-store'
            }
          );

          if (response.ok) {
            const filteredData = await response.json();
            console.log(`Found ${filteredData.length} companies within ${radiusSearchParam.distance}mi of ${radiusSearchParam.location}`);
            setFilteredCompanies(filteredData);

            // Zoom to filtered results
            if (filteredData.length > 0 && mapRef.current) {
              const bounds = new google.maps.LatLngBounds();
              filteredData.forEach((company: Company) => {
                if (company.latitude && company.longitude) {
                  bounds.extend(new google.maps.LatLng(company.latitude, company.longitude));
                }
              });
              mapRef.current.fitBounds(bounds);
            }
          }
        } catch (error) {
          console.error('Radius search error:', error);
        } finally {
          setLoading(false);
        }
      }
    }}
    onClearFilters={() => {
      setAiFilterState({ groups: [], employmentTypes: [] });
    }}
    appliedFilterState={aiFilterState}
    explanation={null}
    isLoading={false}
    projectId={selectedProject?.id}
  />

  {/* Filter Info Banner - Show when filters or radius search is active */}
  {(aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0 || radiusLocation) && (
    <div className="bg-blue-50 border-b border-blue-200 px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-blue-700">
            🗺️ Map showing filtered data
          </span>
          <span className="text-xs text-blue-600">
            {filteredCompanies.length} companies
            {radiusLocation && ` within ${radiusDistance}mi of ${radiusLocation}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setAiFilterState({ groups: [], employmentTypes: [] });
              setRadiusLocation('');
              setRadiusDistance(25);
              setTempRadiusLocation('');
              setTempRadiusDistance(25);
            }}
            className="btn-danger py-1 px-2 text-xs flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Filters
          </button>
          {onNavigateToDataTable && (
            <button
              onClick={() => onNavigateToDataTable(aiFilterState)}
              className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium"
            >
              View in Data Table →
            </button>
          )}
        </div>
      </div>
    </div>
  )}

{/* Export Button - More Compact */}
  <div className="px-3 py-1 border-t bg-gray-50 flex items-center justify-between">
    <span className="text-xs text-gray-600">
      {filteredCompanies.length} companies visible on map
    </span>
    {!IS_RECMAPPER && <button
      onClick={handleExportMapData}
      disabled={filteredCompanies.length === 0}
      className="btn-primary bg-emerald-600 hover:bg-emerald-700 py-1 px-3 text-xs flex items-center gap-2"
    >
      📊 Export Map Data
    </button>}
  </div>

  <FilterBarV2
    columns={getCompanyColumns()}
    candidateColumns={getCandidateColumns()}
    onApplyFilters={(filterState: FilterState) => {
console.log('🎯 Manual filters on Map:', filterState);
  console.log('Employment Types:', filterState.employmentTypes);
  console.log('Filter Groups:', filterState.groups);
      setAiFilterState(filterState);
    }}
    currentDataType="candidates"
    projectId={selectedProject.id}
    initialFilterState={aiFilterState}
    totalRows={filteredCandidates.length}
    currentStartRow={0}
    currentLimit={250}
    onPaginationChange={() => {}}
    currentSort={{ column: 'name', direction: 'asc' }}
    onApplySort={() => {}}
  />
</div>

{/* Radius Search - Moved from old MapFilterBar - More Compact */}
<div className="px-3 py-1 border-b bg-white relative flex-shrink-0">
  <button
    type="button"
    onClick={() => setShowRadiusSearch(!showRadiusSearch)}
    className={`px-3 py-1.5 text-xs rounded-lg transition-all duration-200 ${
      radiusLocation ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/10 font-medium' : 'btn-secondary py-1.5 px-3'
    }`}
  >
    {radiusLocation ? `${radiusDistance}mi radius: ${radiusLocation}` : '📍 Radius search'}
  </button>
  
  {showRadiusSearch && (
    <div className="absolute top-full left-4 mt-1 p-4 border border-gray-200 rounded-xl bg-white shadow-elevated z-50 max-w-md animate-fade-in">
      <div className="mb-2">
        <label className="block text-xs font-medium mb-1">Search by</label>
        <div className="flex gap-2">
          <label className="flex items-center text-xs cursor-pointer">
            <input
              type="radio"
              checked={tempRadiusType === 'company'}
              onChange={() => setTempRadiusType('company')}
              className="mr-1"
            />
            Company offices
          </label>
          <label className="flex items-center text-xs cursor-pointer">
            <input
              type="radio"
              checked={tempRadiusType === 'candidate'}
              onChange={() => setTempRadiusType('candidate')}
              className="mr-1"
            />
            Candidate locations
          </label>
        </div>
      </div>

      <div className="mb-2">
        <label className="block text-xs font-medium mb-1">Location</label>
        <input
          type="text"
          value={tempRadiusLocation}
          onChange={(e) => setTempRadiusLocation(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.closest('div')?.parentElement?.querySelector('button')?.click();
            }
          }}
          placeholder="e.g. London, UK"
          className="input text-xs py-1.5"
        />
      </div>
      
      <div className="mb-2">
        <label className="block text-xs font-medium mb-1">Distance (miles)</label>
        <input
          type="number"
          min="1"
          max="500"
          value={Number.isFinite(tempRadiusDistance) && tempRadiusDistance > 0 ? tempRadiusDistance : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') { setTempRadiusDistance(NaN); return; }
            const n = parseInt(v, 10);
            setTempRadiusDistance(Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : NaN);
          }}
          className="input text-xs py-1.5"
          placeholder="Enter distance in miles"
        />
      </div>

      <div className="flex gap-2">
<button
  type="button"
  onClick={async () => {
    // Copy temp state to real state. If the user cleared the distance
    // input mid-edit (NaN), fall back to a sensible default of 25 miles.
    const effectiveDistance = Number.isFinite(tempRadiusDistance) && tempRadiusDistance > 0
      ? tempRadiusDistance
      : 25;
    setRadiusLocation(tempRadiusLocation);
    setRadiusDistance(effectiveDistance);
    setRadiusType(tempRadiusType);

    if (!tempRadiusLocation) return;

    try {
      setLoading(true);

      // Geocode the location to get center coordinates for the circle
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: tempRadiusLocation }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          setRadiusCenterCoords({ lat: location.lat(), lng: location.lng() });
        }
      });

      // Use backend geocoding via the radius search endpoint
      const params = new URLSearchParams({
        filterType: tempRadiusType === 'company' ? 'companies' : 'candidates',
        radiusType: tempRadiusType,
        radiusLocation: tempRadiusLocation,
        distance: effectiveDistance.toString()
      });

      const response = await fetch(
        `/api/companies/${selectedProject.id}/filtered?${params.toString()}`,
        {
          headers: getAuthHeaders(),
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        alert('Radius search failed. Please check the location and try again.');
        setLoading(false);
        return;
      }

      const filteredData = await response.json();

      console.log(`Found ${filteredData.length} companies within ${effectiveDistance}mi of ${tempRadiusLocation}`);

      setFilteredCompanies(filteredData);
      setShowRadiusSearch(false);

      // Update filter state with radius search info
      setAiFilterState(prev => ({
        ...prev,
        radiusSearch: {
          location: tempRadiusLocation,
          distance: effectiveDistance,
          type: tempRadiusType
        }
      }));

      // Zoom map to show filtered results
      if (filteredData.length > 0 && mapRef.current) {
        const bounds = new google.maps.LatLngBounds();
        filteredData.forEach((company: Company) => {
          if (company.latitude && company.longitude) {
            bounds.extend(new google.maps.LatLng(company.latitude, company.longitude));
          }
        });
        mapRef.current.fitBounds(bounds);
      }

    } catch (error) {
      console.error('Radius search error:', error);
      alert('Radius search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }}
  className="btn-primary flex-1 py-1.5 px-3 text-xs"
>
  Apply
</button>
        {radiusLocation && (
          <button
            type="button"
            onClick={() => {
              setRadiusLocation('');
              setRadiusDistance(25);
              setRadiusCenterCoords(null); // Clear circle from map
              // Clear radius search from filter state
              setAiFilterState(prev => ({
                ...prev,
                radiusSearch: undefined
              }));
              applyColorFilter();
            }}
            className="btn-secondary py-1.5 px-3 text-xs"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  )}
</div>

      {/* Phones: map on top, company list full width underneath */}
      <div className="flex flex-1 relative overflow-hidden min-h-[700px] max-md:flex-col max-md:min-h-0 max-md:overflow-visible">
        <div data-tour="project-map" className="flex-1 relative h-full max-md:flex-none max-md:h-[60vh]">
  <GoogleMap
    mapContainerStyle={mapContainerStyle}
    center={mapCenter}
    zoom={mapZoom}
    onLoad={onMapLoad}
    options={{
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
    }}
  >
<CompanyPinLayer
  map={mapObj}
  companies={filteredCompanies as PinCompany[]}
  selectedId={selectedCompany ? selectedCompany.id : null}
  visitedIds={visitedCompanyIds}
  onSelect={(c) => { setPinGroup(null); handleCompanySelect(c as Company); }}
  onGroupClick={(group) => setPinGroup({ companies: group as Company[], filter: '' })}
/>

{/* Radius search circle overlay */}
{radiusCenterCoords && radiusLocation && (
  <Circle
    center={radiusCenterCoords}
    radius={radiusDistance * 1609.34} // Convert miles to meters
    options={{
      strokeColor: '#3B82F6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3B82F6',
      fillOpacity: 0.1,
      clickable: false,
      zIndex: 1
    }}
  />
)}
</GoogleMap>
{/* Companies sharing one spot (same postcode / town centre): pick one. */}
{pinGroup && (() => {
  const q = pinGroup.filter.trim().toLowerCase();
  const list = pinGroup.companies
    .filter((c) => !q || c.company_name.toLowerCase().includes(q))
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
  const place = pinGroup.companies[0].formatted_address || pinGroup.companies[0].office_address || '';
  return (
    <div className="absolute top-3 left-3 z-20 w-80 max-w-[calc(100%-24px)] bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col" style={{ maxHeight: '70%' }}>
      <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2 border-b border-gray-100">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">{pinGroup.companies.length} companies here</div>
          {place && <div className="text-[11px] text-gray-500 truncate" title={place}>{place}</div>}
        </div>
        <button className="text-gray-400 hover:text-gray-700 text-lg leading-none" onClick={() => setPinGroup(null)} aria-label="Close">×</button>
      </div>
      {pinGroup.companies.length > 8 && (
        <input
          className="mx-3 mt-2 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Filter companies…"
          value={pinGroup.filter}
          onChange={(e) => setPinGroup({ ...pinGroup, filter: e.target.value })}
          autoFocus
        />
      )}
      <div className="overflow-y-auto py-1">
        {list.map((c) => (
          <button
            key={c.id}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${selectedCompany && selectedCompany.id === c.id ? 'bg-blue-50' : ''}`}
            onClick={() => { setPinGroup(null); handleCompanySelect(c); }}
          >
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.pin_color || '#3B82F6' }} />
            <span className={`truncate flex-1 ${visitedCompanyIds.has(c.id) ? 'text-violet-700' : 'text-gray-800'}`}>{c.company_name}</span>
            {(c.candidate_count || 0) > 0 && <span className="text-[11px] text-gray-400 tabular-nums">{c.candidate_count}</span>}
          </button>
        ))}
        {!list.length && <div className="px-3 py-2 text-xs text-gray-400">No company matches “{pinGroup.filter}”.</div>}
      </div>
    </div>
  );
})()}
{/* Office pins placed from postcode / town data are GeoNames (CC BY 4.0), which asks for credit. */}
<a href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer"
  className="absolute bottom-0 left-1/2 -translate-x-1/2 z-[5] text-[10px] text-gray-600 bg-white/70 px-1.5 rounded-t hover:text-gray-900">
  Office locations: GeoNames
</a>
{/* Pin Groups Filter Bar - Collapsible */}
{pinGroups.length > 0 && (
  <div className={`absolute bottom-4 left-4 right-[420px] max-md:right-4 max-md:left-2 max-md:bottom-2 card z-10 transition-all duration-300 ${
    filterGroupsCollapsed ? 'p-2' : 'p-3'
  }`}>
    <div className="flex items-center gap-3 mb-2">
      <button
        onClick={() => setFilterGroupsCollapsed(!filterGroupsCollapsed)}
        className="btn-ghost text-xs py-1 px-2 flex items-center gap-1"
      >
        {filterGroupsCollapsed ? '▶' : '▼'} Filter by Group
      </button>
      {!filterGroupsCollapsed && (
        <>
          <button
            onClick={() => {
              const allColors = new Set(pinGroups.map(g => g.color));
              setVisibleColors(allColors);
              localStorage.setItem(`pinGroups_${selectedProject.id}`, JSON.stringify(Array.from(allColors)));
            }}
            className="btn-ghost text-xs py-1 px-2 text-blue-600 hover:bg-blue-50"
          >
            Show All
          </button>
          <button
            onClick={() => {
              setVisibleColors(new Set());
              localStorage.setItem(`pinGroups_${selectedProject.id}`, JSON.stringify([]));
            }}
            className="btn-ghost text-xs py-1 px-2"
          >
            Hide All
          </button>
        </>
      )}
    </div>

    {!filterGroupsCollapsed && (
      <div className="flex gap-3 overflow-x-auto pb-2 max-h-32 overflow-y-auto">
        {pinGroups.map((group) => (
          <label
            key={group.color}
            className="flex items-center gap-2 cursor-pointer whitespace-nowrap bg-gray-50/80 px-3 py-2 rounded-xl hover:bg-blue-50/40 transition-all duration-200 flex-shrink-0"
          >
            <input
              type="checkbox"
              checked={visibleColors.has(group.color)}
              onChange={(e) => {
                const newVisible = new Set(visibleColors);
                if (e.target.checked) {
                  newVisible.add(group.color);
                } else {
                  newVisible.delete(group.color);
                }
                setVisibleColors(newVisible);
                // Don't save empty state - just remove the saved state
                if (newVisible.size === 0) {
                  localStorage.removeItem(`pinGroups_${selectedProject.id}`);
                } else {
                  localStorage.setItem(`pinGroups_${selectedProject.id}`, JSON.stringify(Array.from(newVisible)));
                }
              }}
              className="w-4 h-4"
            />
            <div
              className="w-6 h-6 rounded border-2 border-white shadow-sm"
              style={{ backgroundColor: group.color }}
            />
            <span className="text-sm text-gray-700 font-medium">
              {group.name || 'Unnamed Group'}
            </span>
          </label>
        ))}
      </div>
    )}
  </div>
)}
      </div>
        <button
          onClick={() => setRightPanelOpen(!rightPanelOpen)}
          className={`absolute top-4 z-50 bg-white shadow-card-hover rounded-l-lg p-2 border border-gray-200 border-r-0 transition-all duration-300 hover:bg-gray-50 max-md:hidden ${
            rightPanelOpen ? 'right-96' : 'right-0'
          }`}
        >
          {rightPanelOpen ? '→' : '←'}
        </button>

        <div className={`transition-all duration-300 bg-white border-l border-gray-200 shadow-elevated ${
          rightPanelOpen ? 'w-96' : 'w-0'
        } overflow-hidden flex flex-col max-md:w-full max-md:border-l-0 max-md:border-t max-md:max-h-[75vh]`}>
          {rightPanelView === 'list' ? (
            <div data-tour="map-company-list" className="flex flex-col flex-1 min-h-0">
              <div className="p-3 border-b bg-gray-50 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
<h3 className="text-base font-semibold">
  Companies ({sortedGroupedCompanies.length.toLocaleString()})
</h3>
                  <select
                    value={companySortBy}
                    onChange={(e) => setCompanySortBy(e.target.value as 'name' | 'count-desc' | 'count-asc')}
                    className="input w-auto text-xs py-1 px-2"
                  >
                    <option value="name">A-Z</option>
                    <option value="count-desc">Most Candidates</option>
                    <option value="count-asc">Least Candidates</option>
                  </select>
                </div>
                <p className="text-xs text-gray-600">Click any company to view details</p>
              </div>
              
              <div ref={scrollContainerRef} className="overflow-y-auto flex-1">
                {sortedGroupedCompanies.slice(0, listLimit).map((group) => {
                  const hasMultipleLocations = group.locations.length > 1;
                  
                  // Sort locations by candidate count (descending)
                  const sortedLocations = [...group.locations].sort((a, b) => {
                    const aCount = companyCandidateCounts.get(a.id)?.total || 0;
                    const bCount = companyCandidateCounts.get(b.id)?.total || 0;
                    return bCount - aCount;
                  });
                  
                  const topLocation = sortedLocations[0];
                  const topLocationCount = companyCandidateCounts.get(topLocation.id)?.total || 0;
                  
                  return (
                    <div key={group.company_name} className="border-b border-gray-100">
                      <div className="p-3 hover:bg-blue-50/40 transition-colors duration-200">
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => hasMultipleLocations ? toggleGroupExpanded(group.company_name) : handleCompanySelect(group.locations[0])}
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-sm text-gray-900 truncate flex items-center gap-2">
                                {hasMultipleLocations && (
                                  <span className="text-gray-400 transition-transform" style={{ 
                                    transform: group.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    display: 'inline-block'
                                  }}>
                                    ▶
                                  </span>
                                )}
                                {group.company_name}
                              </div>
                              {group.totalCandidates > 0 && (
  <span className={`ml-2 px-1.5 py-0.5 text-xs rounded flex-shrink-0 ${
    (aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0) && group.filteredCandidates < group.totalCandidates
      ? 'bg-orange-100 text-orange-800'
      : 'bg-blue-100 text-blue-800'
  }`}>
{(aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0) ? 
  `${group.filteredCandidates}/${group.totalCandidates}` : 
  group.totalCandidates}
  </span>
)}
                            </div>
                            
                            {hasMultipleLocations && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {group.locations.length} locations
                              </div>
                            )}
                            
                            {!hasMultipleLocations && (
                              <>
                                {group.locations[0].industry && (
                                  <div className="text-xs text-gray-600 mt-0.5 truncate">
                                    {group.locations[0].industry}
                                  </div>
                                )}
                                {group.locations[0].employee_count && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {group.locations[0].employee_count.toLocaleString()} employees
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {hasMultipleLocations && group.expanded && (
                        <div className="bg-gray-50 border-t border-gray-200">
                          {sortedLocations.map((location, idx) => {
                            const counts = companyCandidateCounts.get(location.id);
                            const isTopLocation = idx === 0 && topLocationCount > 0;
                            
                            return (
                              <div
                                key={location.id}
                                onClick={() => handleCompanySelect(location)}
                                className={`p-3 pl-8 border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer transition-colors duration-200 ${
                                  isTopLocation ? 'bg-blue-50/60' : ''
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <div className="text-sm text-gray-700 truncate">
                                        📍 {location.formatted_address}
                                      </div>
                                      {counts && counts.total > 0 && (
  <span className={`ml-2 px-1.5 py-0.5 text-xs rounded flex-shrink-0 ${
    isTopLocation ? 'bg-blue-200 text-blue-900' : 'bg-blue-50 text-blue-700'
  }`}>
    {(aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0) ? 
      `${counts.filtered}/${counts.total}` : 
      counts.total}
  </span>
)}
                                    </div>
                                    {location.employee_count && (
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        {location.employee_count.toLocaleString()} employees
                                      </div>
                                    )}
                                    {location.industry && (
                                      <div className="text-xs text-gray-600 mt-0.5 truncate">
                                        {location.industry}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {sortedGroupedCompanies.length > listLimit && (
                  <button
                    onClick={() => setListLimit((n) => n + LIST_PAGE * 5)}
                    className="w-full py-3 text-xs font-medium text-blue-600 hover:bg-blue-50"
                  >
                    Show more ({(sortedGroupedCompanies.length - listLimit).toLocaleString()} not shown — zoom in or filter to narrow)
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {!selectedPerson ? (
                <div className="sticky top-0 z-10 p-3 border-b bg-gray-50 flex-shrink-0">
                  <div className="flex items-center mb-2">
                    <button
                      onClick={handleBackToList}
                      className="btn-ghost text-blue-600 hover:text-blue-700 text-sm py-1 px-2"
                    >
                      ← Back to List
                    </button>
                  </div>

                  <div className="flex items-start space-x-2 mb-3">
                    {selectedCompany?.logo_url && (
                      <img
                        src={selectedCompany.logo_url}
                        alt={`${selectedCompany.company_name} logo`}
                        className="w-10 h-10 rounded flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold truncate">{selectedCompany?.company_name}</h3>
                      {selectedCompany?.industry && (
                        <p className="text-xs text-gray-600 truncate">{selectedCompany.industry}</p>
                      )}
                    </div>
                  </div>

                  {/* 3-Tab Navigation */}
                  <div className="flex space-x-1 bg-gray-100/80 p-1 rounded-lg">
                    <button
                      onClick={() => setActiveTab('company')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                        activeTab === 'company'
                          ? 'tab-active py-1.5 px-2 text-xs'
                          : 'tab-inactive py-1.5 px-2 text-xs'
                      }`}
                    >
                      Company
                    </button>
                    <button
                      onClick={() => setActiveTab('candidates')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                        activeTab === 'candidates'
                          ? 'tab-active py-1.5 px-2 text-xs'
                          : 'tab-inactive py-1.5 px-2 text-xs'
                      }`}
                    >
                      Candidates ({filteredCandidates.length !== allCandidates.length ? `${filteredCandidates.length}/${allCandidates.length}` : allCandidates.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('managers')}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                        activeTab === 'managers'
                          ? 'tab-active py-1.5 px-2 text-xs'
                          : 'tab-inactive py-1.5 px-2 text-xs'
                      }`}
                    >
                      Hiring Mgrs ({hiringManagers.length})
                    </button>
                  </div>
                </div>
              ) : (
                <div className="sticky top-0 z-10 p-3 border-b bg-gray-50 flex-shrink-0">
                <div className="flex items-center mb-2">
                  <button
                    onClick={handleBackToPeople}
                    className="btn-ghost text-blue-600 hover:text-blue-700 text-sm py-1 px-2"
                  >
                      ← Back to {personType === 'candidate' ? 'Candidates' : 'Hiring Managers'}
                    </button>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    {selectedCompany?.logo_url && (
                      <img 
                        src={selectedCompany.logo_url} 
                        alt={`${selectedCompany.company_name} logo`}
                        className="w-8 h-8 rounded flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold truncate">{selectedCompany?.company_name}</h3>
                      {selectedCompany?.industry && (
                        <p className="text-xs text-gray-600 truncate">{selectedCompany.industry}</p>
                      )}
                      {selectedCompany?.website && (
                        <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate block">
                          {selectedCompany.website}
                        </a>
                      )}
                      {selectedCompany?.url && (
                        <a href={selectedCompany.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

<div className="p-3 flex-1 overflow-y-auto">
  {!selectedPerson && enrichmentMessage && (
    <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
      {enrichmentMessage}
    </div>
  )}
                <div className="space-y-2">
                  {selectedPerson ? (
                    <div className="card p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                          {selectedPerson.name}
                        </h4>
                        {/* Star button for managers */}
                        {personType === 'manager' && (
                          <button
                            onClick={(e) => handleToggleStar((selectedPerson as HiringManager).id, e)}
                            className="text-2xl hover:scale-110 transition-transform flex-shrink-0"
                            title={starredManagerIds.has((selectedPerson as HiringManager).id) ? 'Remove star' : 'Star as decision maker'}
                          >
                            {starredManagerIds.has((selectedPerson as HiringManager).id) ? '⭐' : '☆'}
                          </button>
                        )}
                      </div>
                      
                      {personType === 'candidate' ? (
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs font-medium text-gray-700">Position:</label>
                            <p className="text-sm text-gray-900">{(selectedPerson as Candidate).position}</p>
                          </div>
                          
                          {(selectedPerson as Candidate).headline && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Headline:</label>
                              <p className="text-xs text-gray-600 italic">"{(selectedPerson as Candidate).headline}"</p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs font-medium text-gray-700">Employment:</label>
<p className="text-xs text-gray-900">{(selectedPerson as Candidate).current_contract_type || (selectedPerson as Candidate).employment_type || 'Not specified'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700">Work Style:</label>
                              <p className="text-xs text-gray-900">{(selectedPerson as Candidate).working_style}</p>
                            </div>
                          </div>

                          {(selectedPerson as Candidate).address && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Address:</label>
                              <p className="text-xs text-gray-900">{(selectedPerson as Candidate).address}</p>
                            </div>
                          )}

                          {(selectedPerson as Candidate).company_location && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Work Location:</label>
{selectedCompany && findMatchingCompanyLocation((selectedPerson as Candidate).company_location, selectedCompany.office_address || '') ? (
                                <button
                                  onClick={() => selectedCompany && handleLocationClick(selectedCompany)}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {(selectedPerson as Candidate).company_location}
                                </button>
                              ) : (
                                <p className="text-xs text-gray-900">{(selectedPerson as Candidate).company_location}</p>
                              )}
                            </div>
                          )}
                          
{((selectedPerson as Candidate).email || (selectedPerson as Candidate).personal_email) && (
  <div>
    <label className="text-xs font-medium text-gray-700">Email:</label>
    <div className="space-y-1">
{(selectedPerson as Candidate).email && (
  <div>
    <span className="text-xs text-gray-500">Work: </span>
    <a 
      href={`mailto:${(selectedPerson as Candidate).email}`}
      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
    >
      {(selectedPerson as Candidate).email}
    </a>
  </div>
)}
{(selectedPerson as Candidate).personal_email && (
  <div>
    <span className="text-xs text-gray-500">Personal: </span>
    <a 
      href={`mailto:${(selectedPerson as Candidate).personal_email}`}
      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
    >
      {(selectedPerson as Candidate).personal_email}
    </a>
  </div>
)}
    </div>
  </div>
)}
                          
                          {(selectedPerson as Candidate).phone && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Phone:</label>
                              <p className="text-xs text-gray-900">{(selectedPerson as Candidate).phone}</p>
                            </div>
                          )}
                          
                          {(selectedPerson as Candidate).all_skills && (selectedPerson as Candidate).all_skills.length > 0 && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Skills:</label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(selectedPerson as Candidate).all_skills.map((skill, index) => (
                                  <span key={index} className="badge badge-blue">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}                          
                          {(selectedPerson as Candidate).summary && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Summary:</label>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-1 max-h-24 overflow-y-auto">
                                {(selectedPerson as Candidate).summary}
                              </div>
                            </div>
                          )}
                          
                          {(selectedPerson as Candidate).current_role_description && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Current Role:</label>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-1 max-h-24 overflow-y-auto">
                                {(selectedPerson as Candidate).current_role_description}
                              </div>
                            </div>
                          )}

{(selectedPerson as Candidate).li_profile_url && (
  <div className="flex space-x-2">
    <a 
      href={(selectedPerson as Candidate).li_profile_url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="btn-primary py-1.5 px-3 text-xs"
    >
      View LinkedIn Profile
    </a>
{(!((selectedPerson as Candidate).email) || !((selectedPerson as Candidate).personal_email) || !((selectedPerson as Candidate).phone)) && (
  <div className="relative">
<button
  onClick={() => setShowEnrichMenu(showEnrichMenu === selectedPerson.id ? null : selectedPerson.id)}
  disabled={enriching === selectedPerson.id || enrichmentMessage !== null}
  className="btn-primary bg-emerald-600 hover:bg-emerald-700 py-1.5 px-3 text-xs flex items-center gap-1"
>
  {enriching === selectedPerson.id ? '⏳ Starting...' :
   enrichmentMessage ? '🔍 In Progress...' :
   '✨ Enrich Contact'}
  {enriching !== selectedPerson.id && !enrichmentMessage && <span>▼</span>}
</button>

    {showEnrichMenu === selectedPerson.id && (
  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-elevated z-50 min-w-[200px] overflow-hidden animate-fade-in">
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'work_email')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      💼 Work Email <span className="text-gray-500">(1 credit)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'personal_email')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      🏠 Personal Email <span className="text-gray-500">(3 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      📱 Phone <span className="text-gray-500">(12 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'work_email_phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      💼+📱 Work + Phone <span className="text-gray-500">(13 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'personal_email_phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      🏠+📱 Personal + Phone <span className="text-gray-500">(15 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'candidate', 'all')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 transition-colors duration-150"
    >
🔍 Everything <span className="text-gray-500">(16 credits)</span>
    </button>
  </div>
)}
      </div>
    )}
  </div>
)}

                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs font-medium text-gray-700">Job Title:</label>
                            <p className="text-sm text-gray-900">{(selectedPerson as HiringManager).job_title}</p>
                          </div>
                          
                          {(selectedPerson as HiringManager).headline && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Headline:</label>
                              <p className="text-xs text-gray-600 italic">"{(selectedPerson as HiringManager).headline}"</p>
                            </div>
                          )}
                          
                          {(selectedPerson as HiringManager).location && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Location:</label>
                              <p className="text-xs text-gray-900">{(selectedPerson as HiringManager).location}</p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 gap-2">
{((selectedPerson as HiringManager).email || (selectedPerson as HiringManager).personal_email) && (
  <div>
    <label className="text-xs font-medium text-gray-700">Email:</label>
    <div className="space-y-1">
{(selectedPerson as HiringManager).email && (
  <div>
    <span className="text-xs text-gray-500">Work: </span>
    <a 
      href={`mailto:${(selectedPerson as HiringManager).email}`}
      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
    >
      {(selectedPerson as HiringManager).email}
    </a>
  </div>
)}
{(selectedPerson as HiringManager).personal_email && (
  <div>
    <span className="text-xs text-gray-500">Personal: </span>
    <a 
      href={`mailto:${(selectedPerson as HiringManager).personal_email}`}
      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
    >
      {(selectedPerson as HiringManager).personal_email}
    </a>
  </div>
)}
    </div>
  </div>
)}
                            
                            {(selectedPerson as HiringManager).phone && (
                              <div>
                                <label className="text-xs font-medium text-gray-700">Phone:</label>
                                <p className="text-xs text-gray-900">{(selectedPerson as HiringManager).phone}</p>
                              </div>
                            )}
                          </div>
                          
                          {(selectedPerson as HiringManager).summary && (
                            <div>
                              <label className="text-xs font-medium text-gray-700">Summary:</label>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-1 max-h-24 overflow-y-auto">
                                {(selectedPerson as HiringManager).summary}
                              </div>
                            </div>
                          )}
                          
<div className="flex flex-wrap gap-2">
  {(selectedPerson as HiringManager).li_profile_url && (
    <a 
      href={(selectedPerson as HiringManager).li_profile_url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="btn-primary py-1.5 px-2 text-xs"
    >
      LinkedIn Profile
    </a>
  )}

  {(selectedPerson as HiringManager).li_company_url && (
    <a
      href={(selectedPerson as HiringManager).li_company_url}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-secondary py-1.5 px-2 text-xs"
    >
      Company Page
    </a>
  )}

{(!((selectedPerson as HiringManager).email) || !((selectedPerson as HiringManager).personal_email) || !((selectedPerson as HiringManager).phone)) && (
  <div className="relative">
<button
  onClick={() => setShowEnrichMenu(showEnrichMenu === selectedPerson.id ? null : selectedPerson.id)}
  disabled={enriching === selectedPerson.id || enrichmentMessage !== null}
  className="btn-primary bg-emerald-600 hover:bg-emerald-700 py-1.5 px-2 text-xs flex items-center gap-1"
>
  {enriching === selectedPerson.id ? '⏳ Starting...' :
   enrichmentMessage ? '🔍 In Progress...' :
   '✨ Enrich'}
  {enriching !== selectedPerson.id && !enrichmentMessage && <span>▼</span>}
</button>

    {showEnrichMenu === selectedPerson.id && (
  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-elevated z-50 min-w-[200px] overflow-hidden animate-fade-in">
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'work_email')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      💼 Work Email <span className="text-gray-500">(1 credit)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'personal_email')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      🏠 Personal Email <span className="text-gray-500">(3 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      📱 Phone <span className="text-gray-500">(12 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'work_email_phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      💼+📱 Work + Phone <span className="text-gray-500">(13 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'personal_email_phone')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 border-b border-gray-100 transition-colors duration-150"
    >
      🏠+📱 Personal + Phone <span className="text-gray-500">(15 credits)</span>
    </button>
    <button
      onClick={() => handleEnrichContact(selectedPerson.id, 'manager', 'all')}
      className="block w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50/40 transition-colors duration-150"
    >
      🔍 Everything <span className="text-gray-500">(16 credits)</span>
    </button>
  </div>
)}
  </div>
)}
</div>
                        </div>
                      )}
                    </div>
                  ) : activeTab === 'company' ? (
                    /* Company Tab Content */
                    <div className="space-y-3 text-xs">
                      {/* Address */}
                      <p className="text-gray-600">{selectedCompany?.formatted_address}</p>

                      {/* Compact stats row */}
                      <div className="grid grid-cols-2 gap-2">
                        {selectedCompany?.employee_count && (
                          <div><span className="font-semibold">Employees:</span> {selectedCompany.employee_count.toLocaleString()}</div>
                        )}
                        {selectedCompany?.founded && (
                          <div><span className="font-semibold">Founded:</span> {selectedCompany.founded}</div>
                        )}
                      </div>

                      {/* Links row */}
                      <div className="flex gap-3">
                        {selectedCompany?.website && (
                          <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                            Website
                          </a>
                        )}
                        {selectedCompany?.url && (
                          <a href={selectedCompany.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                            LinkedIn
                          </a>
                        )}
                      </div>

                      {/* Collapsible sections */}
                      <div className="space-y-1">
                        {selectedCompany?.description && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Description</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                              {selectedCompany.description}
                            </div>
                          </details>
                        )}

                        {selectedCompany?.specialties && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Specialties</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                              {selectedCompany.specialties}
                            </div>
                          </details>
                        )}

                        {selectedCompany?.about && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">About</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                              {selectedCompany.about}
                            </div>
                          </details>
                        )}

                        {selectedCompany?.investors && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Investors</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                              {selectedCompany.investors}
                            </div>
                          </details>
                        )}

                        {/* Enhanced Description from Clay AI */}
                        {(selectedCompany as any)?.enhanced_description && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">AI-Enhanced Description</summary>
                            <div className="mt-1 text-gray-600 bg-blue-50 p-2 rounded max-h-40 overflow-y-auto">
                              {(selectedCompany as any).enhanced_description}
                            </div>
                          </details>
                        )}

                        {/* Business Intelligence */}
                        {((selectedCompany as any)?.business_type || (selectedCompany as any)?.business_stage ||
                          (selectedCompany as any)?.pattern_tags || (selectedCompany as any)?.scale_scope) && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Business Intelligence</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-40 overflow-y-auto space-y-1">
                              {(selectedCompany as any)?.business_type && Array.isArray((selectedCompany as any).business_type) && (
                                <div><span className="font-semibold">Business Type:</span> {(selectedCompany as any).business_type.join(', ')}</div>
                              )}
                              {(selectedCompany as any)?.business_stage && (
                                <div><span className="font-semibold">Stage:</span> {(selectedCompany as any).business_stage}</div>
                              )}
                              {(selectedCompany as any)?.pattern_tags && (
                                <div><span className="font-semibold">Tags:</span> {(selectedCompany as any).pattern_tags}</div>
                              )}
                              {(selectedCompany as any)?.scale_scope && (
                                <div><span className="font-semibold">Scale & Scope:</span> {(selectedCompany as any).scale_scope}</div>
                              )}
                            </div>
                          </details>
                        )}

                        {/* Financial Information */}
                        {((selectedCompany as any)?.annual_revenue || (selectedCompany as any)?.funding_range ||
                          (selectedCompany as any)?.company_size_formatted) && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Financial Info</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-40 overflow-y-auto space-y-1">
                              {(selectedCompany as any)?.company_size_formatted && (
                                <div><span className="font-semibold">Size:</span> {(selectedCompany as any).company_size_formatted}</div>
                              )}
                              {(selectedCompany as any)?.annual_revenue && (
                                <div><span className="font-semibold">Annual Revenue:</span> ${(selectedCompany as any).annual_revenue}</div>
                              )}
                              {(selectedCompany as any)?.funding_range && (
                                <div><span className="font-semibold">Funding:</span> {(selectedCompany as any).funding_range}</div>
                              )}
                            </div>
                          </details>
                        )}

                        {/* Products & Services */}
                        {((selectedCompany as any)?.primary_offerings || (selectedCompany as any)?.revenue_streams) && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Products & Services</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-40 overflow-y-auto space-y-1">
                              {(selectedCompany as any)?.primary_offerings && Array.isArray((selectedCompany as any).primary_offerings) && (
                                <div>
                                  <span className="font-semibold">Primary Offerings:</span>
                                  <ul className="ml-4 mt-1 list-disc">
                                    {(selectedCompany as any).primary_offerings.map((offering: string, idx: number) => (
                                      <li key={idx}>{offering}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {(selectedCompany as any)?.revenue_streams && Array.isArray((selectedCompany as any).revenue_streams) && (
                                <div>
                                  <span className="font-semibold">Revenue Streams:</span>
                                  <ul className="ml-4 mt-1 list-disc">
                                    {(selectedCompany as any).revenue_streams.map((stream: string, idx: number) => (
                                      <li key={idx}>{stream}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </details>
                        )}

                        {/* Industry Classification */}
                        {(selectedCompany as any)?.subindustry && Array.isArray((selectedCompany as any).subindustry) && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Industry Classification</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                              {(selectedCompany as any).subindustry.join(', ')}
                            </div>
                          </details>
                        )}

                        {/* Company Details */}
                        {(selectedCompany?.slogan || selectedCompany?.primary_category || selectedCompany?.headquarters ||
                          selectedCompany?.funding || selectedCompany?.type || selectedCompany?.crunchbase_url ||
                          selectedCompany?.office_address) && (
                          <details className="text-xs">
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">More Details</summary>
                            <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded max-h-40 overflow-y-auto space-y-1">
                              {selectedCompany?.slogan && (
                                <div className="italic">"{selectedCompany.slogan}"</div>
                              )}
                              {selectedCompany?.office_address && (
                                <div><span className="font-semibold">Office:</span> {selectedCompany.office_address}</div>
                              )}
                              {selectedCompany?.primary_category && (
                                <div><span className="font-semibold">Category:</span> {selectedCompany.primary_category}</div>
                              )}
                              {selectedCompany?.headquarters && (
                                <div><span className="font-semibold">Headquarters:</span> {selectedCompany.headquarters}</div>
                              )}
                              {selectedCompany?.type && (
                                <div><span className="font-semibold">Type:</span> {selectedCompany.type}</div>
                              )}
                              {selectedCompany?.funding && (
                                <div><span className="font-semibold">Funding:</span> {selectedCompany.funding}</div>
                              )}
                              {selectedCompany?.crunchbase_url && (
                                <div>
                                  <span className="font-semibold">Crunchbase:</span>{' '}
                                  <a href={selectedCompany.crunchbase_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                    View Profile
                                  </a>
                                </div>
                              )}
                            </div>
                          </details>
                        )}

                        {/* Custom Columns */}
                        {customColumns.length > 0 && selectedCompany?.custom_data && Object.keys(selectedCompany.custom_data).length > 0 && (
                          <details className="text-xs" open>
                            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">Custom Data</summary>
                            <div className="mt-1 text-gray-600 bg-purple-50 p-2 rounded max-h-60 overflow-y-auto space-y-1">
                              {customColumns.map((col) => {
                                const value = selectedCompany.custom_data?.[col.column_key];
                                if (value === null || value === undefined || value === '') return null;

                                // Format value based on type
                                let displayValue: React.ReactNode = value;
                                if (col.column_type === 'number' && typeof value === 'number') {
                                  displayValue = value.toLocaleString();
                                } else if (col.column_type === 'date' && value) {
                                  displayValue = new Date(value).toLocaleDateString();
                                } else if (Array.isArray(value)) {
                                  displayValue = value.join(', ');
                                } else if (typeof value === 'object') {
                                  displayValue = JSON.stringify(value);
                                }

                                return (
                                  <div key={col.column_key}>
                                    <span className="font-semibold">{col.column_label}:</span> {displayValue}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>

                      {/* Change Pin Color Button */}
                      <div className="mt-3 pt-3 border-t">
                        <button
                          onClick={() => {
                            setSelectedPinColor(selectedCompany?.pin_color || '#3B82F6');
                            setShowPinColorModal(true);
                          }}
                          className="btn-primary bg-purple-600 hover:bg-purple-700 w-full text-sm flex items-center justify-center gap-2"
                        >
                          Change Pin Color
                        </button>
                      </div>
                    </div>
                  ) : activeTab === 'candidates' ? (
                    <>
                      {filteredCandidates.length < allCandidates.length && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
                          <p className="text-xs text-blue-800">
                            Showing {filteredCandidates.length} of {allCandidates.length} candidates matching filters.
                            <button
                              onClick={() => {
                                setCandidates(allCandidates);
                                setFilteredCandidates(allCandidates);
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200"
                            >
                              View all
                            </button>
                          </p>
                        </div>
                      )}
                      {candidates.length > 0 ? (
                          candidates.map((candidate) => {
const matchesLocation = selectedCompany && findMatchingCompanyLocation(candidate.company_location, selectedCompany.office_address || '');                            
                            return (
                              <div 
                                key={candidate.id} 
                                onClick={() => handlePersonClick(candidate, 'candidate')}
                                className={`p-2.5 rounded-xl cursor-pointer hover:bg-blue-50/40 transition-all duration-200 hover:shadow-sm ${
                                  matchesLocation ? 'bg-green-50 border border-green-200' : 'bg-gray-50/80'
                                }`}
                              >
                                <div className="font-medium text-sm text-gray-900 truncate">{candidate.name}</div>
                                <div className="text-xs text-gray-600 mt-0.5 truncate">{candidate.position}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {candidate.current_contract_type || candidate.employment_type || 'Not specified'} • {candidate.working_style}
                                </div>
                                
                                {candidate.company_location && (
                                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                                    📍 {candidate.company_location}
                                  </div>
                                )}
                                
                                {candidate.headline && (
                                  <div className="text-xs text-gray-600 mt-0.5 italic truncate">
                                    "{candidate.headline}"
                                  </div>
                                )}
                                
                                <div className="text-xs text-blue-500 mt-1">Click to view details →</div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-gray-500 py-8 text-sm">
                            No candidates found for this company
                          </div>
)}
</>
                      ) : (
                        hiringManagers.length > 0 ? (
                          // Sort starred managers to the top
                          [...hiringManagers].sort((a, b) => {
                            if (a.is_starred && !b.is_starred) return -1;
                            if (!a.is_starred && b.is_starred) return 1;
                            return 0;
                          }).map((manager) => (
                            <div
                              key={manager.id}
                              onClick={() => handlePersonClick(manager, 'manager')}
                              className={`p-2.5 rounded-xl cursor-pointer transition-all duration-200 relative hover:shadow-sm ${
                                starredManagerIds.has(manager.id) ? 'bg-amber-50/80 hover:bg-amber-50' : 'bg-gray-50/80 hover:bg-blue-50/40'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm text-gray-900 truncate flex items-center gap-1">
                                    {manager.name}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-0.5 truncate">{manager.job_title}</div>

                                  {manager.headline && (
                                    <div className="text-xs text-gray-600 mt-0.5 italic truncate">
                                      "{manager.headline}"
                                    </div>
                                  )}

                                  {manager.location && (
                                    <div className="text-xs text-gray-500 mt-0.5 truncate">{manager.location}</div>
                                  )}

                                  <div className="text-xs text-blue-500 mt-1">Click to view details →</div>
                                </div>

                                {/* Star button */}
                                <button
                                  onClick={(e) => handleToggleStar(manager.id, e)}
                                  className="text-xl hover:scale-110 transition-transform flex-shrink-0"
                                  title={starredManagerIds.has(manager.id) ? 'Remove star' : 'Star as decision maker'}
                                >
                                  {starredManagerIds.has(manager.id) ? '⭐' : '☆'}
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center text-gray-500 py-8 text-sm">
                            No hiring managers found for this company
                          </div>
                        )
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
{/* Pin Color Change Modal */}
      {showPinColorModal && selectedCompany && (
        <div className="modal-backdrop flex items-center justify-center">
          <div className="modal-dialog p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Change Pin Color for {selectedCompany.company_name}
            </h3>
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-800">
                💡 You can change the color for just this office or all offices of this company.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Color
              </label>
              <div className="grid grid-cols-6 gap-2">
                {pinColorPalette.map(color => (
                  <button
                    key={color.hex}
                    onClick={() => setSelectedPinColor(color.hex)}
                    className={`w-full aspect-square rounded-md transition-all ${
                      selectedPinColor === color.hex 
                        ? 'ring-4 ring-blue-500 ring-offset-2' 
                        : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-gray-600">Selected:</span>
                <div 
                  className="w-8 h-8 rounded border border-gray-300"
                  style={{ backgroundColor: selectedPinColor }}
                />
                <span className="text-sm font-mono text-gray-700">{selectedPinColor}</span>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleChangePinColor(true)}
                className="btn-primary bg-purple-700 hover:bg-purple-800 w-full"
              >
                🏢 Change Color for ALL Offices
              </button>
              <button
                onClick={() => handleChangePinColor(false)}
                className="btn-primary bg-purple-600 hover:bg-purple-700 w-full"
              >
                🎨 Change Color for This Office Only
              </button>
              <button
                onClick={() => setShowPinColorModal(false)}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapContainer;
